import React from 'react'
import { Avatar, Chip, Grid, ListItem, ListItemAvatar, ListItemText, Stack, Typography, useTheme, SvgIcon } from '@mui/material'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import ServiceTypeAvatar from 'components/common/avatar/ServiceTypeAvatar'
import { getPricingPeriod, getRecurrenceText } from 'utils/service'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import ServiceMaterialTypeChip from 'components/services/ServiceMaterialTypeChip'
import ServiceActionTypeChip from 'components/services/ServiceActionTypeChip'
import BusinessLineChip from 'components/business-line/BusinessLineChip'
import MeasureChip from 'containers/new-route-manager/RouteCard/MeasureChip'
import MethodChip from 'components/common/chip/MethodChip'
import { getMeasureUnit } from 'utils/get-measure-unit'
import { ReactComponent as RecurrenceIcon } from 'assets/Recurrence.svg'
import { ALPHA_38_PERCENT } from 'theme/colors'

const CSPricingListViewCell = params => {
  const { row } = params
  const {
    actionType,
    businessLine,
    materialColor = '#f1a050',
    materialType,
    methodName,
    methodType,
    periodInterval,
    periodPer,
    recurrenceFrequency: frequency,
    recurrenceInterval: interval,
    recurrencePer: per,
    serviceName,
    serviceType,
    unit,
    value,
    volume,
  } = row || {}
  const globalBusinessLines = useSelector(state => get(state, 'GlobalSearchReducer.globalFilterOptions.businessLines', []))
  const matchedBusinessLine = globalBusinessLines.find(bl => bl.businessLineLabel === businessLine)
  const businessLineType = matchedBusinessLine?.businessLineIcon ? matchedBusinessLine.businessLineIcon.replace('.svg', '') : ''
  const measureText = volume ? `${volume} ${getMeasureUnit(unit)}` : ''
  const theme = useTheme()
  const recurrenceText = getRecurrenceText(per, interval, frequency)
  const pricingPeriod = getPricingPeriod(periodPer, periodInterval)

  return (
    <Grid container sx={{ width: '100%', overflow: 'hidden' }}>
      <Grid size="grow" sx={{ minWidth: 0, overflow: 'hidden' }}>
        <ListItem disableGutters disablePadding>
          <ListItemAvatar sx={{ minWidth: 'unset', pr: 2, flexShrink: 0 }}>
            <Stack sx={{ height: '100%' }} alignItems="center" justifyContent="center">
              <ServiceTypeAvatar serviceType={serviceType} />
            </Stack>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Stack direction="row" alignItems="center" sx={{ overflow: 'hidden', flexWrap: 'nowrap', pb: '0.5em' }}>
                <Typography variant="body1" color="textPrimary" noWrap sx={{ flexShrink: 1, minWidth: 0, pr: 1 }}>
                  {serviceName}
                </Typography>
                {businessLine && <BusinessLineChip businessLineName={businessLine} size="small" businessLineType={businessLineType} />}
              </Stack>
            }
            disableTypography
            secondary={
              <Stack direction="row" spacing={0.5} justifyContent="flex-start" sx={{ overflow: 'hidden', flexWrap: 'nowrap' }}>
                {materialType && (
                  <ServiceMaterialTypeChip
                    sx={{ ...theme.typography.caption, color: 'text.primary', flexShrink: 0 }}
                    display="inline-flex"
                    label={materialType}
                    materialColor={materialColor}
                    size="small"
                  />
                )}
                {measureText && <MeasureChip size="small" measureUnit={unit} label={measureText} />}
                {actionType && <ServiceActionTypeChip action={actionType} size="small" />}
                {(methodType || methodName) && <MethodChip size="small" methodType={methodType || methodName} />}
                {recurrenceText && (
                  <Chip
                    size="small"
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                    avatar={
                      <Avatar sx={{ backgroundColor: `${theme.palette.background.black}${ALPHA_38_PERCENT}` }}>
                        <SvgIcon inheritViewBox sx={{ fontSize: 12, color: theme.palette.background.paper }}>
                          <RecurrenceIcon />
                        </SvgIcon>
                      </Avatar>
                    }
                    label={recurrenceText}
                  />
                )}
              </Stack>
            }
          />
        </ListItem>
      </Grid>
      <Grid size="auto">
        <ListItem>
          <ListItemText
            primary={<HHDisplayMoney value={value} formatToDollars={false} />}
            secondary={pricingPeriod}
            slotProps={{
              primary: {
                color: 'textPrimary',
                fontWeight: 700,
                textAlign: 'right',
                variant: 'h5',
                whiteSpace: 'nowrap',
              },
              secondary: {
                color: 'textSecondary',
                fontWeight: 400,
                textAlign: 'right',
                variant: 'h5',
                whiteSpace: 'nowrap',
              },
            }}
          />
        </ListItem>
      </Grid>
    </Grid>
  )
}

CSPricingListViewCell.propTypes = {}

export default CSPricingListViewCell
